// Audit log viewer — 05-admin §2.3. GET /v1/admin/audit (admin).
// actor / subject / action / period filters, before→after diff, trace_id deep link.
import { api, errMsg } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import type { AuditEntry, Page } from "../models.js";

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

// before→after diff view (05 §2.3).
function diffDialog(entry: AuditEntry): void {
  const backdrop = h("div", { class: "dialog-backdrop" });
  function close(): void {
    backdrop.remove();
  }
  function pretty(json?: string | null): string {
    if (!json) return "—";
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }
  backdrop.appendChild(
    h(
      "div",
      { class: "dialog wide" },
      h("h3", {}, entry.action),
      h("p", { class: "muted small" }, `subject: ${entry.subject_type ?? "—"}/${entry.subject_id ?? "—"}`),
      entry.trace_id ? h("p", { class: "small" }, "trace_id: ", h("span", { class: "mono" }, entry.trace_id)) : null,
      h(
        "div",
        { class: "diff" },
        h("div", {}, h("h4", {}, "before"), h("pre", {}, pretty(entry.before_json))),
        h("div", {}, h("h4", {}, "after"), h("pre", {}, pretty(entry.after_json))),
      ),
      h("div", { class: "row" }, h("button", { class: "btn secondary", onclick: close }, "닫기")),
    ),
  );
  document.body.appendChild(backdrop);
}

export async function auditView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const actorField = h("input", { class: "field", placeholder: "actor_id" }) as HTMLInputElement;
  const subjectField = h("input", { class: "field", placeholder: "subject_id" }) as HTMLInputElement;
  const listWrap = h("div", { class: "card" });

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "감사 로그 불러오는 중…"));
    try {
      // Backend GET /v1/admin/audit supports actor + subject_id filters (01 §3.M).
      const qs = new URLSearchParams({ limit: "50" });
      if (actorField.value.trim()) qs.set("actor", actorField.value.trim());
      if (subjectField.value.trim()) qs.set("subject_id", subjectField.value.trim());
      const page = await api.get<Page<AuditEntry>>(`/v1/admin/audit?${qs.toString()}`);
      if (page.items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, "감사 로그가 없습니다."));
        return;
      }
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "시각"),
            h("th", {}, "actor"),
            h("th", {}, "action"),
            h("th", {}, "subject"),
            h("th", {}, "trace_id"),
            h("th", {}, ""),
          ),
        ),
        h(
          "tbody",
          {},
          ...page.items.map((e) =>
            h(
              "tr",
              {},
              h("td", { class: "muted small" }, fmtTime(e.created_at)),
              h("td", { class: "mono small" }, e.actor_id ?? "—"),
              h("td", {}, h("span", { class: "badge" }, e.action)),
              h("td", { class: "small" }, e.subject_type ? `${e.subject_type}/${e.subject_id ?? ""}` : "—"),
              h("td", { class: "mono small" }, e.trace_id ?? "—"),
              h("td", {}, h("button", { class: "btn small", onclick: () => diffDialog(e) }, "diff")),
            ),
          ),
        ),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      listWrap.replaceChildren(h("div", { class: "state error" }, `감사 로그 불러오기 실패: ${errMsg(e)}`));
    }
  }

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "감사 로그"),
      h(
        "p",
        { class: "muted" },
        "운영자의 모든 상태 변경 액션은 불변 레코드로 기록됩니다 (05-admin §2.3, admin 전용). append-only.",
      ),
      h(
        "div",
        { class: "row wrap" },
        actorField,
        subjectField,
        h("button", { class: "btn", onclick: () => void load() }, "필터 적용"),
      ),
      listWrap,
    ),
  );
  await load();
}
